import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeout = useRef(null);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const getSessionToken = () => {
    return document.cookie.split('; ').find(row => row.startsWith('session_token='))?.split('=')[1];
  };

  const fetchNotifications = useCallback(async () => {
    const token = getSessionToken();
    if (!token) return;
    try {
      const [notifRes, countRes] = await Promise.all([
        fetch(`${API}/notifications`, { credentials: 'include', headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/notifications/unread-count`, { credentials: 'include', headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (notifRes.ok) {
        const data = await notifRes.json();
        setNotifications(data.notifications || []);
      }
      if (countRes.ok) {
        const data = await countRes.json();
        setUnreadCount(data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [API]);

  const connectWebSocket = useCallback(() => {
    const token = getSessionToken();
    if (!token || !user) return;

    // Build WebSocket URL
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = new URL(process.env.REACT_APP_BACKEND_URL).host;
    const wsUrl = `${wsProtocol}//${wsHost}/ws/notifications?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        // Send periodic pings to keep alive
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);
        ws._pingInterval = pingInterval;
      };

      ws.onmessage = (event) => {
        if (event.data === 'pong') return;
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'notification') {
            setNotifications(prev => [message.data, ...prev]);
            setUnreadCount(prev => prev + 1);
          }
        } catch (e) {
          // ignore non-JSON messages
        }
      };

      ws.onclose = () => {
        if (ws._pingInterval) clearInterval(ws._pingInterval);
        // Reconnect after 5 seconds
        reconnectTimeout.current = setTimeout(() => {
          if (user) connectWebSocket();
        }, 5000);
      };

      ws.onerror = () => {
        ws.close();
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('WebSocket connection error:', error);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      connectWebSocket();
    }
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
    };
  }, [user, fetchNotifications, connectWebSocket]);

  const markAsRead = async (notificationId) => {
    const token = getSessionToken();
    if (!token) return;
    try {
      await fetch(`${API}/notifications/${notificationId}/read`, {
        method: 'POST', credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    const token = getSessionToken();
    if (!token) return;
    try {
      await fetch(`${API}/notifications/read-all`, {
        method: 'POST', credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  return (
    <NotificationContext.Provider value={{
      notifications, unreadCount, markAsRead, markAllAsRead, fetchNotifications
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used within a NotificationProvider');
  return context;
}
